import React, { useContext, useEffect, useState, useCallback } from "react";
import { Tabs, Tab, CircularProgress } from "@mui/material";
import { CalendarIcon, ListDashesIcon, PlusIcon, CirclesThreeIcon, CaretLeftIcon, CaretRightIcon, EraserIcon } from "@phosphor-icons/react";
import axios from "axios";
import { AuthContext } from "../../contexts";
import { toast } from "react-toastify";
import { toastOptions } from "../../assets/constants";
import TimeTable from "./TimeTable";
import ModelsButton from "./ModelsButton";
import AutomationsButton from "./AutomationsButton";
import { CustomTag, CustomPageHeader, CustomSearchInput } from "../../ui";
import { useDraftEntries } from "./hooks";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import WeekTabLabel from "./WeekTabLabel";

dayjs.extend(isoWeek);
dayjs.extend(utc);
dayjs.extend(timezone);

const TimeEntryList = () => {
    const [tab, setTab] = useState(0);
    const [entries, setEntries] = useState([]);
    const [projects, setProjects] = useState([]);
    const [sections, setSections] = useState([]);
    const [models, setModels] = useState([]);
    const [automations, setAutomations] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeDay, setActiveDay] = useState(dayjs());
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterProject, setFilterProject] = useState(null);
    const { token, user } = useContext(AuthContext);
    const urlParams = new URLSearchParams(window.location.search);
    const asanaAuth = urlParams.get("asana_auth");

    useDraftEntries(entries, setEntries, isDataLoaded);

    useEffect(() => {
        if (asanaAuth && asanaAuth === "success") {
            toast.success("Connexion avec Asana réussie", toastOptions);
            urlParams.delete("asana_auth");
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (asanaAuth && asanaAuth === "failure") {
            toast.error("Connexion avec Asana échouée", toastOptions);
            urlParams.delete("asana_auth");
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }, [asanaAuth, urlParams]);

    const fetchData = React.useCallback(() => {
        setLoading(true);
        axios
            .get(process.env.REACT_APP_UPA_API_HOST + "tracking/data", {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                const apiEntries = response.data.entries.map((entry) => ({
                    ...entry,
                    projectId: entry.task.section.projectId,
                }));
                setEntries((prev) => {
                    const tempEntries = prev.filter((e) => e.tempId);
                    return [...apiEntries, ...tempEntries];
                });
                setProjects(response.data.projects);
                setSections(response.data.sections);
                setTasks(response.data.tasks);
                setModels(response.data.models);
                setAutomations(response.data.automations);
                setLoading(false);
                setIsDataLoaded(true);
            })
            .catch((error) => {
                toast.error(error.response.data.message, toastOptions);
                setLoading(false);
            });
    }, [token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const sortedProjects = React.useMemo(() => {
        if (!projects || projects.length === 0) return [];
        return [...projects].sort((a, b) => a.name.localeCompare(b.name));
    }, [projects]);

    const handleClearFilters = useCallback(() => {
        setSearchQuery("");
        setFilterProject(null);
    }, []);

    const hasActiveFilters = searchQuery || filterProject;

    const handleAddEntry = () => {
        setEntries([...entries, { tempId: Date.now(), date: activeDay.tz("Europe/Paris").format("YYYY-MM-DD"), comments: "", duration: 0 }]);
    };

    const handleReorderEntries = (newEntries) => {
        setEntries(newEntries);
    };

    const getDayHours = (day) => {
        return entries
            .filter((entry) => entry.date === activeDay.isoWeekday(day).tz("Europe/Paris").format("YYYY-MM-DD"))
            .reduce((sum, entry) => {
                const dur = typeof entry.duration === "number" ? entry.duration : parseFloat(entry.duration) || 0;
                return sum + dur;
            }, 0)
            .toFixed(2);
    };

    return !loading ? (
        <div className="c-app__view tracking">
            <CustomPageHeader
                title="Saisie d'activité"
                titleIcon={{ icon: CalendarIcon }}
                customActions={
                    !user.asanaAccessToken ? (
                        <a
                            href={`https://app.asana.com/-/oauth_authorize?client_id=${process.env.REACT_APP_ASANA_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_UPA_API_HOST}asana/auth&response_type=code&state=${user.id}`}
                        >
                            <button className="button-large">
                                <CirclesThreeIcon size={16} weight="fill" color="var(--asana-500)" />
                                Se connecter à Asana
                            </button>
                        </a>
                    ) : (
                        <>
                            <AutomationsButton models={models} automations={automations} setAutomations={setAutomations} />
                            <div className="custom-dropdown-button">
                                <button className="button-large blue-button dropdown-left" onClick={handleAddEntry}>
                                    <PlusIcon size={16} weight="bold" />
                                    Nouvelle saisie
                                </button>
                                <ModelsButton
                                    models={models}
                                    setModels={setModels}
                                    projects={projects}
                                    sections={sections}
                                    tasks={tasks}
                                    entries={entries}
                                    setEntries={setEntries}
                                    automations={automations}
                                    setAutomations={setAutomations}
                                    activeDay={activeDay}
                                />
                            </div>
                        </>
                    )
                }
            />
            {!user.asanaAccessToken && (
                <>
                    <CustomTag value="La connexion à Asana est nécessaire pour commencer la saisie des temps" />
                    <div className="asana-blur-auth" />
                </>
            )}
            <div className="tabs-container">
                <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)}>
                    <Tab value={0} label="Semaine" icon={<CalendarIcon size={16} weight="bold" />} iconPosition="start" />
                    <Tab value={1} label="Tout" icon={<ListDashesIcon size={16} weight="bold" />} iconPosition="start" />
                </Tabs>
                {tab === 0 && (
                    <div style={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "space-between", gap: 8, height: 65 }}>
                        <div className="week-tab-arrows" onClick={() => setActiveDay(activeDay.subtract(1, "week"))}>
                            <CaretLeftIcon size={16} weight="bold" />
                        </div>
                        <Tabs
                            value={activeDay.isoWeekday()}
                            onChange={(event, newValue) => setActiveDay(activeDay.startOf("week").add(newValue - 1, "day"))}
                            className="week-tabs"
                            variant="fullWidth"
                            TabIndicatorProps={{ style: { backgroundColor: "transparent" } }}
                        >
                            <Tab value={1} label={<WeekTabLabel dayName="Lundi" dateStr={activeDay.startOf("week").format("D MMM")} hours={getDayHours(1)} />} />
                            <Tab value={2} label={<WeekTabLabel dayName="Mardi" dateStr={activeDay.startOf("week").add(1, "day").format("D MMM")} hours={getDayHours(2)} />} />
                            <Tab value={3} label={<WeekTabLabel dayName="Mercredi" dateStr={activeDay.startOf("week").add(2, "day").format("D MMM")} hours={getDayHours(3)} />} />
                            <Tab value={4} label={<WeekTabLabel dayName="Jeudi" dateStr={activeDay.startOf("week").add(3, "day").format("D MMM")} hours={getDayHours(4)} />} />
                            <Tab value={5} label={<WeekTabLabel dayName="Vendredi" dateStr={activeDay.startOf("week").add(4, "day").format("D MMM")} hours={getDayHours(5)} />} />
                        </Tabs>
                        <div className="week-tab-arrows" onClick={() => setActiveDay(activeDay.add(1, "week"))}>
                            <CaretRightIcon size={16} weight="bold" />
                        </div>
                    </div>
                )}
                {tab === 1 && (
                    <div className="tracking-filters">
                        <div className="input-container">
                            <div className="single-search-input">
                                <input type="text" placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
                            </div>
                        </div>
                        <div className="filter-container">
                            <CustomSearchInput
                                entity={sortedProjects}
                                value={filterProject ? { value: filterProject.id, label: filterProject.name } : null}
                                onChange={(option) => setFilterProject(option ? projects.find((p) => p.id === option.value) : null)}
                                placeholder="Projet"
                                isClearable
                                noIcon
                            />
                            {hasActiveFilters && (
                                <div className="custom-filter-button" onClick={handleClearFilters}>
                                    <EraserIcon size={12} weight="bold" />
                                    Effacer
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {[0, 1].map((index) => (
                    <div key={index} role="tabpanel" hidden={tab !== index} style={{ height: "100%" }}>
                        <TimeTable
                            entries={entries}
                            setEntries={setEntries}
                            projects={projects}
                            sections={sections}
                            tasks={tasks}
                            handleAddEntry={handleAddEntry}
                            automations={automations}
                            models={models}
                            setModels={setModels}
                            setAutomations={setAutomations}
                            tab={tab}
                            activeDay={activeDay}
                            setActiveDay={setActiveDay}
                            setTab={setTab}
                            onReorderEntries={handleReorderEntries}
                            filterProject={filterProject}
                            searchQuery={searchQuery}
                        />
                    </div>
                ))}
            </div>
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default TimeEntryList;
